// OGSDecoder — a composable ONNX decoder node (geom / appr / …). Wraps a
// DecoderSession and declares graph ports, so decoders chain: one decoder's
// `code` output feeds the next decoder's `code` input (the _IdentityExp2Code
// seam from multi_part_gaussian_model.py). Runtime creates the session in init;
// tests inject one.

import type {
  DirtySource,
  FrameInputs,
  Node,
  NodeContext,
  NodeTensor,
  PortSpec,
} from "../types";
import { PrecisionCache } from "../decoderBuild.ts";
import type { DecoderSession } from "../DecoderSession";

export interface OGSDecoderOptions {
  id: string;
  inputs: string[]; // input port names (bound by edges), e.g. ["code","plucker"]
  outputs: string[]; // output port names produced, e.g. ["geom_uv","code"]
  dirtyOn?: DirtySource[]; // epoch sources; [] = dirty only via upstream edges
  // Runtime path: build the session from ctx. Test path: pass one in directly.
  session?: DecoderSession;
  createSession?: (ctx: NodeContext) => Promise<DecoderSession>;
  /** Precision-aware build (the decoderFactories path). When present, init and
   *  setPrecision go through it + a warm PrecisionCache, so the Avatar node's
   *  fp16/fp32 dropdown is an instant reference swap after the first build. */
  createSessionFor?: (
    ctx: NodeContext,
    want16: boolean,
  ) => Promise<{ session: DecoderSession; usedFp16: boolean }>;
  /** Precision preference at init (the factory's want16). */
  want16?: boolean;
}

export class OGSDecoder implements Node {
  readonly id: string;
  readonly type = "ogsDecoder";
  readonly inputs: readonly PortSpec[];
  readonly outputs: readonly PortSpec[];
  readonly bindings = [];

  private readonly _dirtyOn: DirtySource[];
  private session: DecoderSession | null;
  private readonly createSession?: (ctx: NodeContext) => Promise<DecoderSession>;
  private readonly createSessionFor?: (
    ctx: NodeContext,
    want16: boolean,
  ) => Promise<{ session: DecoderSession; usedFp16: boolean }>;
  private readonly want16: boolean;
  private readonly precisions = new PrecisionCache();
  private ctx: NodeContext | null = null;
  /** Precision the ACTIVE session built at (fp16 auto-downgrades to fp32). */
  usedFp16 = false;

  constructor(opts: OGSDecoderOptions) {
    this.id = opts.id;
    this.inputs = opts.inputs.map((name) => ({ name }));
    this.outputs = opts.outputs.map((name) => ({ name }));
    this._dirtyOn = opts.dirtyOn ?? [];
    this.session = opts.session ?? null;
    this.createSession = opts.createSession;
    this.createSessionFor = opts.createSessionFor;
    this.want16 = !!opts.want16;
  }

  async init(ctx: NodeContext): Promise<void> {
    this.ctx = ctx;
    if (this.session) return;
    if (this.createSessionFor) {
      const res = await this.precisions.get(this.want16, (w) => this.createSessionFor!(ctx, w));
      this.session = res.session;
      this.usedFp16 = res.usedFp16;
      return;
    }
    if (this.createSession) this.session = await this.createSession(ctx);
  }

  /** Swap to a precision without a reload. Returns the RESOLVED precision, or
   *  null when this decoder has no precision-aware builder (injected session).
   *  Caller must ensure no run is in flight (OGSModel's inFlight mutex). */
  async setPrecision(want16: boolean): Promise<boolean | null> {
    if (!this.createSessionFor || !this.ctx) return null;
    const res = await this.precisions.get(want16, (w) => this.createSessionFor!(this.ctx!, w));
    this.session = res.session;
    this.usedFp16 = res.usedFp16;
    return res.usedFp16;
  }

  /** Build + cache a precision WITHOUT activating it, so a later setPrecision to
   *  it is an instant swap. Best-effort background warm-up. */
  async prebuild(want16: boolean): Promise<void> {
    if (!this.createSessionFor || !this.ctx) return;
    await this.precisions.get(want16, (w) => this.createSessionFor!(this.ctx!, w));
  }

  /** Swap the backing session (the live fp16↔fp32 precision toggle). The caller
   *  owns BOTH sessions — the warm precision cache keeps the outgoing one
   *  resident so switching back is a reference swap — so this never disposes. */
  setSession(session: DecoderSession): void {
    this.session = session;
  }

  /** The active session, or null before init / after dispose. */
  get activeSession(): DecoderSession | null {
    return this.session;
  }

  async evaluate(
    _frame: FrameInputs,
    inbound: Readonly<Record<string, NodeTensor>>,
  ): Promise<Record<string, NodeTensor>> {
    if (!this.session) throw new Error(`OGSDecoder "${this.id}": no session (init not run?)`);
    return this.session.run(inbound);
  }

  dirtyOn(): readonly DirtySource[] {
    return this._dirtyOn;
  }

  dispose(): void {
    // Release the ORT session + its live gpu-buffer tensors — nulling the ref
    // alone leaks both, and a character switch tears down eight decoders.
    // Optional-called: tests inject a bare { run } stub.
    // The active session is USUALLY also in the precision cache, so both calls
    // land on it — DecoderSession.dispose() is idempotent for exactly that.
    try { (this.session as { dispose?: () => void } | null)?.dispose?.(); } catch { /* best-effort */ }
    // …and the warm other-precision session, which nothing else owns.
    this.precisions.dispose();
    this.session = null;
    this.ctx = null;
  }
}
